import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import config, { fixImageUrl } from '../config';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { MapPin, DollarSign, Send, ArrowLeft, MessageCircle, Trash, Star, CheckCircle, Flag, Share2, Heart, Edit, Trash2, Link2, Phone, Eye, Clock, Facebook, MessageSquare, Shield, Printer } from 'lucide-react';
import { toast } from 'sonner';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';
import ReviewModal from '../components/ReviewModal';
import ReviewsListModal from '../components/ReviewsListModal';
import ReportButton from '../components/ReportButton';
import EditGigModal from '../components/EditGigModal';
import SEO, { generateBreadcrumbSchema } from '../components/SEO';
import ImageLightbox from '../components/ImageLightbox';

const GigDetails = ({ profile, setProfile }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'en';
  const isCebuano = currentLang === 'ceb';
  const [gig, setGig] = useState(null);

  // Helper to get localized field (Cebuano if available, else English)
  const getLocalizedField = (obj, field) => {
    if (isCebuano && obj?.[`${field}_ceb`]) {
      return obj[`${field}_ceb`];
    }
    return obj?.[field];
  };
  const [loading, setLoading] = useState(true);
  const [showBidDialog, setShowBidDialog] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [showReviewsListDialog, setShowReviewsListDialog] = useState(false);
  const [bidData, setBidData] = useState({ amount: '', timeline: '', description: '' });
  const [bidding, setBidding] = useState(false);
  const [myBid, setMyBid] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [isSaved, setIsSaved] = useState(false);
  
  // Image lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Edit/Delete functionality
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Bids for owner view
  const [bids, setBids] = useState([]);
  const [bidsLoading, setBidsLoading] = useState(false);

  // Mark as filled with bidder selection
  const [showMarkFilledDialog, setShowMarkFilledDialog] = useState(false);
  const [selectedHiredBidder, setSelectedHiredBidder] = useState(null);
  const [markingFilled, setMarkingFilled] = useState(false);

  const isWorker = profile?.role === 'seeker' || profile?.role === 'contractor' || profile?.role === 'job_seeker';
  const isOwner = profile && gig && gig.owner_id === profile.id;
  const isAdmin = profile?.role === 'admin';
  const canEdit = isOwner || isAdmin;

  useEffect(() => {
    fetchGig();
  }, [id]);

  // Check bid status and saved status after gig is loaded
  useEffect(() => {
    if (gig && profile && isWorker) {
      checkMyBid();
      checkIfSaved();
    }
  }, [gig, profile]);

  // Fetch bids for gig owner
  useEffect(() => {
    if (gig && profile && (gig.owner_id === profile.id || profile.role === 'admin')) {
      fetchBids();
    }
  }, [gig, profile]);

  const fetchBids = async () => {
    if (!gig?.id) return;
    setBidsLoading(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) return;

      const response = await api.get(`/api/bids/gig/${gig.id}`, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });
      setBids(response.data.bids || []);
    } catch (error) {
      console.error('Failed to fetch bids:', error);
    } finally {
      setBidsLoading(false);
    }
  };

  const markBidAsViewed = async (bidId) => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) return;
      await api.post(`/api/bids/${bidId}/view`, {}, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });
      setBids(prev =>
        prev.map(b => b.id === bidId ? { ...b, viewed_at: new Date().toISOString() } : b)
      );
    } catch (error) {
      console.error('Failed to mark bid as viewed:', error);
    }
  };

  const handleMarkFilled = async () => {
    if (!gig) return;

    setMarkingFilled(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error('Please log in');
        return;
      }

      await api.post(
        `/api/gigs/${gig.id}/mark-filled`,
        { hired_contractor_id: selectedHiredBidder },
        {
          headers: { Authorization: `Bearer ${session.session.access_token}` }
        }
      );

      if (selectedHiredBidder) {
        toast.success(t('gigs.gig_filled_with_hire') || 'Gig marked as filled! The hired bidder has been notified.');
      } else {
        toast.success(t('gigs.gig_filled_success') || 'Gig marked as filled!');
      }

      setShowMarkFilledDialog(false);
      setSelectedHiredBidder(null);
      fetchGig(); // Refresh gig data
    } catch (error) {
      console.error('Mark filled error:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark gig as filled');
    } finally {
      setMarkingFilled(false);
    }
  };

  const checkIfSaved = async () => {
    if (!profile?.id || !isWorker) return;
    try {
      const { data } = await supabase
        .from('saved_gigs')
        .select('id')
        .eq('user_id', profile.id)
        .eq('gig_id', id)
        .maybeSingle();
      setIsSaved(!!data);
    } catch (error) {
      console.error('Failed to check if saved:', error);
    }
  };

  const toggleSave = async () => {
    if (!isWorker) return;
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) return;

      if (isSaved) {
        await api.delete(`/api/gigs/${id}/save`, {
          headers: { Authorization: `Bearer ${session.session.access_token}` }
        });
        setIsSaved(false);
        toast.success('Gig removed from saved');
      } else {
        await api.post(`/api/gigs/${id}/save`, {}, {
          headers: { Authorization: `Bearer ${session.session.access_token}` }
        });
        setIsSaved(true);
        toast.success('Gig saved!');
      }
    } catch (error) {
      console.error('Toggle save error:', error);
      toast.error('Failed to save gig');
    }
  };

  // Legacy clipboard path: in-app browsers and non-secure contexts have no
  // navigator.clipboard, so a promise-only copy silently does nothing there.
  const legacyCopy = (text, successMessage) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.select();
    let copied = false;
    try {
      // execCommand returns false on a denied copy rather than throwing, so the
      // return value has to be checked or a failed copy reports success.
      copied = document.execCommand('copy');
    } catch (err) {
      copied = false;
    }
    document.body.removeChild(textArea);
    if (copied) {
      toast.success(successMessage, { duration: 4000 });
    } else {
      toast.info(`${t('gigs.share_this_link', 'Share this link:')} ${text}`, { duration: 8000 });
    }
  };

  const copyShareLink = (text) => {
    const successMessage = t('gigs.link_copied', 'Link copied! Paste anywhere to share.');
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text)
        .then(() => toast.success(successMessage, { duration: 4000 }))
        .catch(() => legacyCopy(text, successMessage));
    } else {
      legacyCopy(text, successMessage);
    }
  };

  const handleShare = (platform) => {
    if (!gig) return;

    // Always link to the backend /share endpoint, never the SPA route: that endpoint is
    // what serves the Open Graph tags (image, title, budget) Facebook scrapes for the
    // preview card. Slug-first so the canonical og:url resolves in one hop.
    const shareEndpoint = `${config.BACKEND_URL}/api/gigs/${gig.slug || id}/share`;
    // Format budget range like JobDetails formats salary range
    const budgetText = gig.budget_min && gig.budget_max
      ? `₱${gig.budget_min.toLocaleString()}-₱${gig.budget_max.toLocaleString()}`
      : gig.budget_min ? `₱${gig.budget_min.toLocaleString()}`
      : gig.budget_max ? `₱${gig.budget_max.toLocaleString()}`
      : '';
    const shareText = `${getLocalizedField(gig, 'title')} in ${gig.location}${budgetText ? ` — ${budgetText}` : ''} | ${t('gigs.share_quote_cta', 'Bid now on Bohol Trabaho!')} #BoholJobs #scammerfree`;

    if (platform === 'facebook') {
      // Use FB SDK for Facebook sharing with proper Open Graph tags
      if (typeof window.FB !== 'undefined') {
        window.FB.ui({
          method: 'share',
          href: shareEndpoint,
          quote: shareText,
        }, function(response){
          // Cancelling is not an error: the SDK reports it either as no response at
          // all or as error_code 4201 ("User canceled the Dialog flow"), so neither
          // shape may toast.
          const cancelled = !response || response.error_code === 4201;
          if (cancelled) return;
          if (response.error_message) {
            toast.error(`${t('gigs.share_failed', 'Failed to share')}: ${response.error_message}`);
            // Put the link on the clipboard so a failed share isn't a dead end
            copyShareLink(shareEndpoint);
          } else {
            toast.success(t('gigs.shared_to_facebook', 'Shared to Facebook!'), { duration: 3000 });
          }
        });
        // Closed synchronously: if the SDK never invokes the callback (failed init,
        // popup killed), a callback-only close would strand the dialog open forever.
        setShowShareDialog(false);
        return;
      }

      // Fallback if FB SDK not loaded - use direct dialog with proper centering
      const fbShareUrl = `https://www.facebook.com/dialog/share?app_id=1387027799794224&display=popup&href=${encodeURIComponent(shareEndpoint)}&redirect_uri=${encodeURIComponent(shareEndpoint)}`;

      const width = 600;
      const height = 600;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;

      const popup = window.open(
        fbShareUrl,
        'facebook-share-dialog',
        `width=${width},height=${height},left=${left},top=${top},toolbar=0,status=0,scrollbars=1`
      );

      if (popup) {
        toast.success(t('gigs.opening_facebook', 'Opening Facebook share dialog...'), { duration: 3000 });
        setShowShareDialog(false);
      } else {
        // Popup blocked — fall back to the clipboard so the share isn't a dead end
        copyShareLink(shareEndpoint);
      }
      return;
    }

    if (platform === 'viber') {
      const viberUrl = `viber://forward?text=${encodeURIComponent(shareText + ' ' + shareEndpoint)}`;
      window.open(viberUrl, '_blank');
      setShowShareDialog(false);
      toast.success(t('gigs.opening_viber', 'Opening Viber...'), { duration: 2000 });
      return;
    }

    if (platform === 'copy') {
      copyShareLink(shareEndpoint);
      setShowShareDialog(false);
      return;
    }

    // For other platforms (WhatsApp, Messenger)
    const shareUrls = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareEndpoint)}`,
      messenger: `fb-messenger://share/?link=${encodeURIComponent(shareEndpoint)}`
    };

    window.open(shareUrls[platform], '_blank');
    setShowShareDialog(false);
    if (platform === 'messenger') {
      toast.success(t('gigs.opening_messenger', 'Opening Messenger...'), { duration: 2000 });
    }
  };

  const handleReport = () => {
    if (!reportReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    toast.success('Report submitted. Admin will review shortly.');
    setShowReportDialog(false);
    setReportReason('');
  };

  const fetchGig = async () => {
    try {
      const response = await api.get(`/api/gigs/${id}`);
      setGig(response.data.gig);
    } catch (error) {
      console.error('Failed to fetch gig:', error);
      if (error.response?.status === 404) {
        toast.error('This gig has been deleted or expired');
      } else {
        toast.error('Failed to load gig details');
      }
    } finally {
      setLoading(false);
    }
  };

  const checkMyBid = async () => {
    if (!profile || !gig) return;
    
    try {
      const { data, error } = await supabase
        .from('bids')
        .select('*')
        .eq('gig_id', gig.id) // Use gig.id (UUID) not URL param
        .eq('contractor_id', profile.id)
        .maybeSingle();
      
      if (!error && data) {
        setMyBid(data);
      }
    } catch (error) {
      console.error('Failed to check bid:', error);
    }
  };

  const handleBid = async () => {
    if (!bidData.amount || !bidData.timeline) {
      toast.error('Please fill in all required fields');
      return;
    }

    setBidding(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error('Please log in to bid');
        return;
      }

      await api.post(
        '/api/bids',
        {
          gig_id: id,
          amount: parseFloat(bidData.amount),
          timeline: bidData.timeline,
          description: bidData.description,
          photos: []
        },
        {
          headers: {
            Authorization: `Bearer ${session.session.access_token}`
          }
        }
      );

      toast.success('Bid submitted successfully!');
      setShowBidDialog(false);
      await checkMyBid();
      setTimeout(() => navigate('/dashboard'), 500);
    } catch (error) {
      console.error('Bid error:', error);
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('already submitted')) {
        toast.error('You have already submitted a bid for this gig');
        await checkMyBid();
      } else {
        toast.error(error.response?.data?.detail || 'Failed to submit bid');
      }
    } finally {
      setBidding(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please log in');
        return;
      }

      await api.delete(`/api/gigs/${id}`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });

      toast.success('Gig deleted successfully');
      navigate('/gigs');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete gig');
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleEditSuccess = (updatedGig) => {
    setGig({ ...gig, ...updatedGig });
    toast.success('Gig updated!');
    const newId = updatedGig.slug || updatedGig.id;
    if (newId && newId !== id) {
      navigate(`/gigs/${newId}`, { replace: true });
    }
  };


  const handleWithdrawBid = async () => {
    if (!myBid) return;

    setSubmitting(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error('Please log in');
        return;
      }

      const response = await fetch(`${config.BACKEND_URL}/api/bids/${myBid.id}/withdraw`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.session.access_token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.clone().json();
        throw new Error(errorData.detail || 'Withdraw failed');
      }

      toast.success('Bid withdrawn successfully');
      
      setMyBid({ ...myBid, status: 'withdrawn' });
      
      setTimeout(() => {
        checkMyBid();
      }, 100);
    } catch (error) {
      console.error('Withdraw error:', error);
      toast.error(error.message || 'Failed to withdraw bid');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkCompleted = async () => {
    if (!gig || gig.owner_id !== profile?.id) return;

    setSubmitting(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error('Please log in');
        return;
      }

      await supabase
        .from('gigs')
        .update({ status: 'completed', updated_at: new Date().toISOString() })
        .eq('id', id)
        .eq('owner_id', profile.id);

      toast.success('Gig marked as completed!');
      fetchGig();
    } catch (error) {
      console.error('Error marking gig as completed:', error);
      toast.error('Failed to mark gig as completed');
    } finally {
      setSubmitting(false);
    }
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    const now = new Date();
    const posted = new Date(dateString);
    const diffMs = now - posted;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  if (loading) {
    return (
      <div className="min-h-screen-safe flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!gig) {
    return (
      <div className="min-h-screen-safe pb-nav md:pb-0">
        <TopBar profile={profile} setProfile={setProfile} />
        <div className="flex flex-col items-center justify-center h-96 px-4">
          <h2 className="font-heading text-2xl font-bold mb-2">Gig Not Found</h2>
          <p className="text-muted-foreground mb-4">This gig has been deleted or expired</p>
          <Button onClick={() => navigate('/jobs')} className="rounded-xl">
            Browse Jobs
          </Button>
        </div>
        <BottomNav profile={profile} />
      </div>
    );
  }

  const isContractor = profile?.role === 'contractor' || profile?.role === 'seeker' || profile?.role === 'job_seeker';
  // isOwner already defined above with canEdit
  const canWithdraw = myBid && (myBid.status === 'pending' || myBid.status === 'bid');
  const canChat = myBid && (myBid.status === 'accepted' || myBid.status === 'revealed');
  const gigCompleted = gig.status === 'completed';
  const gigInProgress = gig.status === 'in_progress';
  const gigFilled = gig.status === 'filled' || gig.status === 'hired';
  const hasApplied = !!myBid;
  const isHired = gigInProgress || gigCompleted || gigFilled;

  // Generate SEO schema for gig
  const breadcrumbSchema = gig ? generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Gigs', url: '/gigs' },
    { name: gig.title }
  ]) : null;
  
  // Format budget for SEO description
  const formatBudgetRange = () => {
    if (!gig) return '';
    const min = gig.budget_min ? `₱${gig.budget_min.toLocaleString()}` : '';
    const max = gig.budget_max ? `₱${gig.budget_max.toLocaleString()}` : '';
    if (min && max) return `${min}-${max}`;
    return min || max || '';
  };

  const getMatchColor = (score) => {
    if (score >= 75) return 'bg-green-500';
    if (score >= 50) return 'bg-yellow-500';
    return 'bg-orange-500';
  };

  return (
    <div className="min-h-screen-safe pb-nav md:pb-0">
      {/* SEO: Dynamic meta tags for gig details */}
      {gig && (
        <SEO
          title={`${gig.title} - Gig in ${gig.location}`}
          description={`${gig.title} - Budget: ${formatBudgetRange()} in ${gig.location}, Bohol. ${gig.description?.substring(0, 120) || 'Bid now on Bohol Trabaho!'}`}
          url={`/gigs/${gig.slug || gig.id}`}
          schema={breadcrumbSchema}
          keywords={[
            gig.title,
            `${gig.title} gig`,
            `gigs in ${gig.location}`,
            'Bohol gigs',
            'freelance Bohol',
            'projects Bohol'
          ]}
          publishedTime={gig.created_at}
        />
      )}
      
      <TopBar profile={profile} setProfile={setProfile} />

      <div className="px-4 py-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
          >
            <ArrowLeft size={16} className="mr-2" />
            Back
          </Button>
          
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.print()}
                className="rounded-xl no-print"
              >
                <Printer size={16} className="mr-2" />
                Print
              </Button>
            )}
            {profile && gig && profile.id !== gig.owner_id && (
              <ReportButton itemType="gig" itemId={gig.id} />
            )}
          </div>
        </div>

        <Card>
          <CardContent className="p-4 md:p-6">
            {/* Gig Image */}
            {gig.image_url && (
              <div className="mb-6 -mx-4 md:-mx-6 -mt-4 md:-mt-6 cursor-pointer" onClick={() => setLightboxOpen(true)}>
                <img
                  src={fixImageUrl(gig.image_url)}
                  alt={gig.title}
                  className="w-full h-64 object-cover rounded-t-lg"
                  style={{ objectPosition: `center ${gig.image_position ?? 50}%` }}
                />
              </div>
            )}

            {/* Edit/Delete Buttons (Owner or Admin) */}
            {canEdit && (
              <div className="flex gap-2 mb-4 justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowEditModal(true)}
                  data-testid="edit-gig-btn"
                >
                  <Edit size={16} className="mr-2" />
                  {t('common.edit')}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowDeleteDialog(true)}
                  data-testid="delete-gig-btn"
                >
                  <Trash2 size={16} className="mr-2" />
                  {t('common.delete')}
                </Button>
              </div>
            )}

            {/* Secondary actions. Sharing is not here — it has its own prominent
                CTA down with the bid button, mirroring the jobs page. */}
            <div className="flex items-center gap-2 flex-wrap mb-4">
              {/* Save button - ONLY for worker accounts */}
              {isWorker && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={toggleSave}
                  className="rounded-xl"
                >
                  <Heart size={16} className={`mr-2 ${isSaved ? 'fill-red-500 text-red-500 animate-pulse' : 'text-gray-400'}`} strokeWidth={2.5} />
                  {isSaved ? t('common.saved') : t('common.save')}
                </Button>
              )}

              {/* Natural width, not flex-1: a stretched lone "Report" button
                  over-weights reporting on mobile now that Share has moved. */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowReportDialog(true)}
                className="rounded-xl"
              >
                <Flag size={16} className="mr-2" />
                {t('common.report')}
              </Button>
            </div>
            
            <div className="mb-6">
              <div className="flex items-start justify-between mb-4 gap-3">
                <div className="flex-1 min-w-0">
                  <h1 className="font-heading text-2xl md:text-3xl font-bold mb-2 break-words">{getLocalizedField(gig, 'title')}</h1>
                  <div className="flex items-center gap-2 text-sm md:text-base text-muted-foreground flex-wrap">
                    {gig.owner && (
                      <span className="flex items-center gap-1 break-all">
                        {gig.owner.verified && <Shield size={14} className="text-teal-600 fill-teal-600 flex-shrink-0" title="Verified True Boholano" />}
                        <span className="break-words">{gig.owner.business_name || gig.owner.name || t('common.anonymous_owner')}</span>
                      </span>
                    )}
                  </div>
                  {gig.created_at && (
                    <div className="text-xs md:text-sm text-muted-foreground mt-1">
                      {t('common.posted')} {getTimeAgo(gig.created_at)}
                    </div>
                  )}
                </div>
                {gig.owner?.rating > 0 && (
                  <button
                    onClick={() => setShowReviewsListDialog(true)}
                    className="transition-transform hover:scale-105 cursor-pointer flex-shrink-0"
                  >
                    <Badge className="text-base md:text-lg">⭐ {gig.owner.rating.toFixed(1)}</Badge>
                  </button>
                )}
              </div>

              <div className="flex flex-wrap gap-2 md:gap-3 mb-6">
                <div className="flex items-center text-sm break-all">
                  <MapPin size={14} className="mr-2 text-muted-foreground flex-shrink-0" />
                  <span className="break-words">{gig.location}</span>
                </div>
                {(gig.budget_min || gig.budget_max) && (
                  <div className="flex items-center text-sm font-semibold">
                    <span className="mr-2 text-muted-foreground flex-shrink-0 font-bold">₱</span>
                    <span className="break-all">{gig.budget_min?.toLocaleString() || '0'} - ₱{gig.budget_max?.toLocaleString() || '0'}</span>
                  </div>
                )}
                {isHired ? (
                  <Badge variant="secondary" className="bg-gray-500 text-white">
                    {gigCompleted ? t('gigs.completed') : t('gigs.hired')}
                  </Badge>
                ) : (
                  <Badge variant="secondary">{t('gigs.open_for_bids')}</Badge>
                )}
              </div>
            </div>

            <div className="mb-6">
              <h2 className="font-heading text-lg md:text-xl font-semibold mb-3">{t('gigs.project_description')}</h2>
              <div
                className="text-sm md:text-base text-muted-foreground prose prose-sm max-w-none overflow-x-auto break-words"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(getLocalizedField(gig, 'description') || '') }}
              />
            </div>

            {gig.benefits && gig.benefits.length > 0 && (
              <div className="mb-6" data-testid="gig-benefits-section">
                <h2 className="font-heading text-lg md:text-xl font-semibold mb-3">{t('gigs.benefits')}</h2>
                <div className="flex flex-wrap gap-2">
                  {gig.benefits.map((benefit, i) => (
                    <Badge
                      key={i}
                      variant="secondary"
                      className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20"
                      data-testid={`gig-benefit-${i}`}
                    >
                      {benefit}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Bid Button for Non-Logged-In Users - Redirects to Signup */}
            {!profile && !isHired && (
              <div className="border-t pt-6">
                <Button
                  size="lg"
                  className="w-full rounded-xl"
                  onClick={() => navigate('/auth?mode=signup&role=job_seeker&redirect=' + encodeURIComponent(window.location.pathname))}
                  data-testid="signup-to-bid-btn"
                >
                  <Send size={16} className="mr-2" />
                  {t('gigs.submit_bid')}
                </Button>
              </div>
            )}

            {/* Contractor Actions */}
            {isContractor && !hasApplied && !isHired && (
              <div className="border-t pt-6 space-y-3">
                {/* Bid button - show only if not applied and position not filled */}
                <Button
                  size="lg"
                  className="w-full rounded-xl"
                  onClick={() => setShowBidDialog(true)}
                  data-testid="submit-bid-btn"
                >
                  <Send size={16} className="mr-2" />
                  {t('gigs.submit_bid')}
                </Button>

                {/* Save button - always show for contractors */}
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full rounded-xl"
                  onClick={toggleSave}
                  data-testid="save-gig-btn"
                >
                  <Heart
                    size={18}
                    className={`mr-2 ${isSaved ? 'fill-red-500 text-red-500 animate-pulse' : ''}`}
                  />
                  {isSaved ? t('common.saved') : t('gigs.save_gig')}
                </Button>
              </div>
            )}
            
            {/* Already Applied Message - Show when contractor has submitted a bid but position not filled yet */}
            {isContractor && !hasApplied && isHired && (
              <div className="border-t pt-6">
                <Card className="bg-gray-100 border-gray-300">
                  <CardContent className="p-6 text-center">
                    <CheckCircle size={48} className="mx-auto mb-3 text-gray-500" />
                    <h3 className="font-heading text-lg font-semibold mb-2">{t('gigs.position_filled')}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t('gigs.position_filled_desc')}
                    </p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Share Button - Show to ALL users (including the gig owner) */}
            <div className="border-t pt-6">
              <Button
                size="lg"
                variant="outline"
                className="w-full rounded-xl border-[#1877F2] text-[#1877F2] hover:bg-[#1877F2]/10 transition-colors"
                onClick={() => setShowShareDialog(true)}
                data-testid="share-dropdown-btn"
              >
                <Share2 size={18} className="mr-2" />
                {t('gigs.share_gig')}
              </Button>
            </div>

            {/* Your Application Card */}
            {isContractor && hasApplied && (
              <div className="border-t pt-6">
                <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
                  <CardContent className="p-4 md:p-6">
                    <h3 className="font-heading text-lg md:text-xl font-semibold mb-4 flex items-center gap-2">
                      <span className="text-primary">✓</span>
                      {t('gigs.your_application')}
                    </h3>
                    <div className="space-y-3 mb-4">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <span className="text-sm font-medium">{t('gigs.proposal_amount')}:</span>
                        <span className="text-base md:text-lg font-bold text-primary break-all">₱{myBid.amount?.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <span className="text-sm font-medium">{t('common.timeline')}:</span>
                        <span className="text-sm break-words">{myBid.timeline}</span>
                      </div>
                      {myBid.description && (
                        <div className="pt-2 border-t">
                          <p className="text-sm font-medium mb-1">{t('gigs.cover_letter')}:</p>
                          <p className="text-sm text-muted-foreground break-words">{myBid.description}</p>
                        </div>
                      )}
                      <div className="flex items-center justify-between pt-2 flex-wrap gap-2">
                        <span className="text-sm font-medium">{t('common.status')}:</span>
                        <Badge className={myBid.status === 'accepted' ? 'bg-green-500' : myBid.status === 'withdrawn' ? 'bg-gray-500' : ''}>
                          {myBid.status}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      {(gigInProgress || myBid.status === 'accepted' || myBid.status === 'revealed') && (
                        <Button
                          size="lg"
                          variant="default"
                          className="flex-1 rounded-xl bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => navigate(`/messages?user=${gig.owner_id}`)}
                        >
                          <MessageCircle size={16} className="mr-2" />
                          {t('gigs.chat_with_owner')}
                        </Button>
                      )}
                      {canWithdraw && !isHired && (
                        <Button
                          size="lg"
                          variant="destructive"
                          className="rounded-xl flex-1 sm:flex-none"
                          onClick={handleWithdrawBid}
                          disabled={submitting}
                        >
                          <Trash size={16} className="mr-2" />
                          {t('gigs.withdraw')}
                        </Button>
                      )}
                    </div>
                    {gigCompleted && (
                      <Button
                        size="lg"
                        className="w-full rounded-xl mt-3 bg-yellow-500 hover:bg-yellow-600"
                        onClick={() => setShowReviewDialog(true)}
                      >
                        <Star size={16} className="mr-2" />
                        {t('gigs.leave_review')}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Owner Actions */}
            {isOwner && (
              <div className="border-t pt-6 space-y-3">
                {/* Mark as Filled button for open gigs */}
                {gig.status === 'open' && (
                  <Button
                    size="lg"
                    className="w-full rounded-xl bg-blue-600 hover:bg-blue-700"
                    onClick={() => setShowMarkFilledDialog(true)}
                  >
                    <CheckCircle size={16} className="mr-2" />
                    {t('gigs.mark_filled') || 'Mark as Filled'}
                  </Button>
                )}

                {gigInProgress && (
                  <Button
                    size="lg"
                    className="w-full rounded-xl bg-green-600 hover:bg-green-700"
                    onClick={handleMarkCompleted}
                    disabled={submitting}
                  >
                    <CheckCircle size={16} className="mr-2" />
                    {t('gigs.mark_completed')}
                  </Button>
                )}

                {/* Bids Table */}
                <div className="mt-6">
                  <h3 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
                    <Eye size={20} />
                    {t('gigs.bids_received')} ({bids.length})
                  </h3>

                  {bidsLoading ? (
                    <div className="text-center py-8 text-muted-foreground">Loading bids...</div>
                  ) : bids.length === 0 ? (
                    <Card className="bg-muted/30">
                      <CardContent className="p-6 text-center">
                        <p className="text-muted-foreground">{t('gigs.no_bids_yet')}</p>
                        <p className="text-sm text-muted-foreground mt-1">{t('gigs.be_first_to_bid')}</p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-3">
                      {[...bids].sort((a, b) => {
                        const aNew = !a.viewed_at;
                        const bNew = !b.viewed_at;
                        if (aNew !== bNew) return aNew ? -1 : 1;
                        return new Date(b.created_at) - new Date(a.created_at);
                      }).map((bid) => {
                        const contractor = bid.contractor;
                        const isNew = !bid.viewed_at;

                        return (
                          <Card key={bid.id} className={`cursor-pointer transition-colors ${isNew ? 'border-l-4 border-l-blue-500 bg-blue-50/50' : ''}`} onClick={() => { if (isNew) markBidAsViewed(bid.id); }}>
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-2">
                                    {isNew && (
                                      <Badge variant="secondary" className="bg-blue-100 text-blue-800 text-xs">NEW</Badge>
                                    )}
                                    <span className="font-medium">
                                      {contractor?.name || `Worker from ${contractor?.town || 'Bohol'}`}
                                    </span>
                                    {contractor?.verified && (
                                      <Shield size={14} className="text-teal-600 fill-teal-600 flex-shrink-0" title="Verified True Boholano" />
                                    )}
                                    {contractor?.rating > 0 && (
                                      <span className="text-sm text-yellow-600 flex items-center gap-1">
                                        <Star size={12} className="fill-yellow-500" />
                                        {contractor.rating.toFixed(1)}
                                      </span>
                                    )}
                                  </div>

                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm mb-2">
                                    <div>
                                      <span className="text-muted-foreground">Bid: </span>
                                      <span className="font-semibold text-primary">₱{bid.amount?.toLocaleString()}</span>
                                    </div>
                                    <div>
                                      <span className="text-muted-foreground">Timeline: </span>
                                      <span>{bid.timeline}</span>
                                    </div>
                                  </div>

                                  {bid.description && (
                                    <p className="text-sm text-muted-foreground line-clamp-2">{bid.description}</p>
                                  )}

                                  {contractor?.skills && contractor.skills.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-2">
                                      {contractor.skills.slice(0, 3).map((skill, idx) => (
                                        <Badge key={idx} variant="outline" className="text-xs">{skill}</Badge>
                                      ))}
                                    </div>
                                  )}

                                  {bid.match_score != null && (
                                    <div className="flex items-center gap-1.5 mt-2">
                                      <Badge className={`${getMatchColor(bid.match_score)} text-white text-xs`}>
                                        {bid.match_score}% AI Match
                                      </Badge>
                                      {bid.match_reason && (
                                        <span className="text-xs text-muted-foreground italic truncate max-w-[200px]">
                                          {i18n.language === 'ceb' && bid.match_reason_ceb ? bid.match_reason_ceb : bid.match_reason}
                                        </span>
                                      )}
                                    </div>
                                  )}

                                  <div className="text-xs text-muted-foreground mt-2">
                                    <Clock size={12} className="inline mr-1" />
                                    {new Date(bid.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                  </div>
                                </div>

                                <div className="flex flex-col gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="rounded-xl"
                                    onClick={() => navigate(`/messages?user=${contractor?.id}`)}
                                  >
                                    <MessageCircle size={14} className="mr-1" />
                                    Chat
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('gigs.share_gig')}</DialogTitle>
            <DialogDescription>
              {t('gigs.choose_share_method')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <Button
              className="w-full rounded-xl"
              variant="outline"
              onClick={() => handleShare('facebook')}
              data-testid="share-facebook-btn"
            >
              <Facebook size={18} className="mr-2 text-[#1877F2]" fill="#1877F2" />
              {t('gigs.share_facebook')}
            </Button>
            <Button
              className="w-full rounded-xl"
              variant="outline"
              onClick={() => handleShare('messenger')}
              data-testid="share-messenger-btn"
            >
              <MessageSquare size={18} className="mr-2 text-[#0099FF]" />
              {t('gigs.share_messenger')}
            </Button>
            <Button
              className="w-full rounded-xl"
              variant="outline"
              onClick={() => handleShare('viber')}
              data-testid="share-viber-btn"
            >
              <Phone size={18} className="mr-2 text-[#7360F2]" />
              {t('gigs.share_viber') || 'Share to Viber'}
            </Button>
            <Button
              className="w-full rounded-xl"
              variant="outline"
              onClick={() => handleShare('whatsapp')}
              data-testid="share-whatsapp-btn"
            >
              <MessageCircle size={18} className="mr-2 text-[#25D366]" />
              {t('gigs.share_whatsapp')}
            </Button>
            <Button
              className="w-full rounded-xl"
              variant="outline"
              onClick={() => handleShare('copy')}
              data-testid="copy-link-btn"
            >
              <Link2 size={18} className="mr-2 text-gray-600" />
              {t('gigs.copy_link') || 'Copy Shareable Link'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bid Dialog */}
      <Dialog open={showBidDialog} onOpenChange={setShowBidDialog}>
        <DialogContent data-testid="bid-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('gigs.submit_your_bid')}</DialogTitle>
            <DialogDescription>
              {t('gigs.propose_price_timeline')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="amount">{t('gigs.bid_amount')} (₱) *</Label>
              <Input
                id="amount"
                type="number"
                placeholder="5000"
                value={bidData.amount}
                onChange={(e) => setBidData({ ...bidData, amount: e.target.value })}
                onWheel={(e) => e.target.blur()}
                className="rounded-xl"
                data-testid="bid-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="timeline">{t('common.timeline')} *</Label>
              <Input
                id="timeline"
                type="text"
                placeholder={t('gigs.timeline_placeholder')}
                value={bidData.timeline}
                onChange={(e) => setBidData({ ...bidData, timeline: e.target.value })}
                className="rounded-xl"
                data-testid="bid-timeline-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">{t('gigs.additional_details')}</Label>
              <Textarea
                id="description"
                placeholder={t('gigs.explain_approach')}
                value={bidData.description}
                onChange={(e) => setBidData({ ...bidData, description: e.target.value })}
                rows={4}
                className="rounded-xl"
                data-testid="bid-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowBidDialog(false)}
              disabled={bidding}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleBid}
              disabled={bidding}
              data-testid="submit-bid-confirm-btn"
            >
              {bidding ? t('common.submitting') : t('gigs.submit_bid')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <ReviewModal
        open={showReviewDialog}
        onOpenChange={setShowReviewDialog}
        targetUser={gig.owner}
        context="gig"
        contextId={id}
        onSuccess={() => {
          toast.success('Review submitted!');
          setShowReviewDialog(false);
        }}
      />

      {/* Reviews List Dialog */}
      <ReviewsListModal
        open={showReviewsListDialog}
        onOpenChange={setShowReviewsListDialog}
        userId={gig.owner_id}
        userName={gig.owner?.business_name || gig.owner?.name}
      />

      {/* Report Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('gigs.report_gig')}</DialogTitle>
            <DialogDescription>
              {t('gigs.report_reason_desc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="report-reason">{t('gigs.reason_for_reporting')} *</Label>
              <Textarea
                id="report-reason"
                placeholder={t('gigs.describe_report_reason')}
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                rows={4}
                className="rounded-xl"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowReportDialog(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleReport}
              variant="destructive"
            >
              {t('gigs.submit_report')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Gig Modal */}
      <EditGigModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        gig={gig}
        onSuccess={handleEditSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('gigs.delete_gig_post')}</DialogTitle>
            <DialogDescription>
              {t('gigs.delete_confirmation')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={deleting}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? t('common.deleting') : t('gigs.delete_gig')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark as Filled Dialog with Bidder Selection */}
      <Dialog open={showMarkFilledDialog} onOpenChange={(open) => {
        setShowMarkFilledDialog(open);
        if (!open) setSelectedHiredBidder(null);
      }}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('gigs.mark_filled') || 'Mark Gig as Filled'}</DialogTitle>
            <DialogDescription className="space-y-3 pt-2">
              <div className="bg-muted p-3 rounded-lg">
                <p className="font-medium text-foreground">{gig?.title}</p>
                <p className="text-sm text-muted-foreground mt-1">{gig?.location}</p>
              </div>
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {bids.length > 0 ? (
              <>
                <p className="text-sm font-medium mb-3">
                  {t('dashboard.whichBidderHired') || 'Which bidder did you hire?'}
                </p>
                <p className="text-xs text-muted-foreground mb-4">
                  {t('dashboard.selectHiredPersonDesc') || 'Select the person you hired so they can receive a congratulations notification and you can both review each other.'}
                </p>

                <div className="space-y-2 max-h-[300px] overflow-y-auto border rounded-lg p-2">
                  {/* Option to skip selection */}
                  <div
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedHiredBidder === null
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                    onClick={() => setSelectedHiredBidder(null)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        selectedHiredBidder === null ? 'border-primary bg-primary' : 'border-gray-300'
                      }`}>
                        {selectedHiredBidder === null && (
                          <div className="w-2 h-2 rounded-full bg-white" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{t('dashboard.skipAddLater') || 'Skip - add later'}</p>
                        <p className="text-xs text-muted-foreground">
                          {t('dashboard.justMarkFilled', { type: 'gig' }) || 'Just mark the gig as filled'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Bidder options */}
                  {bids.map((bid) => {
                    const contractor = bid.contractor;

                    return (
                      <div
                        key={bid.id}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedHiredBidder === contractor?.id
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                        }`}
                        onClick={() => setSelectedHiredBidder(contractor?.id)}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            selectedHiredBidder === contractor?.id ? 'border-primary bg-primary' : 'border-gray-300'
                          }`}>
                            {selectedHiredBidder === contractor?.id && (
                              <div className="w-2 h-2 rounded-full bg-white" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm">
                                {contractor?.name || `Worker from ${contractor?.town || 'Bohol'}`}
                              </p>
                              {contractor?.verified && (
                                <Shield size={14} className="text-teal-600 fill-teal-600 flex-shrink-0" title="Verified True Boholano" />
                              )}
                              {contractor?.rating > 0 && (
                                <span className="text-xs text-yellow-600 flex items-center gap-1">
                                  <Star size={10} className="fill-yellow-500" />
                                  {contractor.rating.toFixed(1)}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Bid: ₱{bid.amount?.toLocaleString()} · {bid.timeline}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                No bids received yet. You can still mark this gig as filled.
              </p>
            )}

            <p className="text-sm text-muted-foreground mt-4">
              {t('dashboard.markedFilledRemoved', { type: 'gig' }) || 'Once marked as filled, this gig will be removed from search results.'}
            </p>
          </div>

          <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowMarkFilledDialog(false);
                setSelectedHiredBidder(null);
              }}
              disabled={markingFilled}
              className="flex-1"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleMarkFilled}
              disabled={markingFilled}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              <CheckCircle size={16} className="mr-2" />
              {markingFilled ? t('common.submitting') : (selectedHiredBidder ? t('dashboard.confirmMarkFilled') || 'Confirm & Mark Filled' : t('dashboard.markAsFilled') || 'Mark as Filled')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Lightbox */}
      {gig?.image_url && (
        <ImageLightbox
          src={fixImageUrl(gig.image_url)}
          alt={gig.title}
          isOpen={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
        />
      )}

      <BottomNav profile={profile} />
    </div>
  );
};

export default GigDetails;
